import React from 'react';
import moment from 'moment';
// @ts-ignore
import { emojify } from 'react-emojione';
import { ILumaBotFlowMessagesHistory, ELumaBotFlowMessagesHistorySource, TUser } from 'ui-model';

import { WithStyles, withStyles } from '@material-ui/core';
import classnames from 'classnames';
import { useCurrentUser } from '@lib/hooks';
import configService from '@lib/services/configService';
import storageService from '@lib/services/storageService';

import { RolloutEnum } from '@lib/resources/types';

import usePermissions from 'hooks/usePermissions';
import { muiThemeLuma } from 'themes';
import { Typography } from 'components/v2';
import AvatarLumaBot from 'components/v2/AvatarLumaBot';

import styles from './Message.styles';
import { parseMessageContent } from './Messages.utils';
import { MessageAvatar } from '../MessageAvatar';

export interface MessageProps extends WithStyles<typeof styles>, ILumaBotFlowMessagesHistory {
  patient: TUser;
}

const Message: React.FC<MessageProps> = ({ classes, text = '', source, createdAt, patient }) => {
  const currentUser = useCurrentUser();
  const { hasAccessToProduct } = usePermissions();

  const isLumaBot = source === ELumaBotFlowMessagesHistorySource.LumaBot;

  let messageHtml = text;

  if (!isLumaBot) {
    // Links & URLs clickable for `internal` messages only
    const disableLinks = !hasAccessToProduct(RolloutEnum.ChatInternalLinks); // CHECK THIS
    messageHtml = parseMessageContent(messageHtml, disableLinks);

    // highlight templates
    messageHtml = messageHtml.replace(
      /({{[^}]+}})/gm,
      `<span style="background-color: #f1f1f1; border-radius: 4px; color: ${muiThemeLuma.custom.palette.colorDarkCyan}; font-weight: 400;">$1</span>`,
    );
  }

  messageHtml = messageHtml.replace(
    /(:[\w]+:)/gm,
    (match) => `<span style="font-size: 14px;">${emojify(match, { output: 'unicode' })}</span>`,
  );

  const lumaBotSettings = React.useMemo(
    () => ({
      name: currentUser?.settings?.lumabot.name,
      avatar: `${configService.getApiBasePath()}/fileUploads/${
        currentUser?.settings?.lumabot?.avatar
      }/file?token=${storageService.getToken()}`,
    }),
    [currentUser],
  );

  return (
    <div className={classes.root}>
      <div className={classnames(classes.wrapper, { [classes.outbound]: isLumaBot })}>
        {!isLumaBot && patient && (
          <div className={classes.pullBottom}>
            <MessageAvatar interlocutor={patient} size="xsmall" />
          </div>
        )}
        <div
          className={classnames(classes.messageWrapper, {
            [classes.outbound]: isLumaBot,
          })}
        >
          <div
            className={classes.messageContent}
            dangerouslySetInnerHTML={{ __html: messageHtml }} // eslint-disable-line react/no-danger
          />
          <Typography variant="supportW500F12" color="inherit">
            {moment(createdAt).format('MMM DD, YYYY h:mm A')}
          </Typography>
        </div>
        {isLumaBot && (
          <div className={classes.pullBottom}>
            <AvatarLumaBot src={currentUser?.settings?.lumabot?.avatar ? lumaBotSettings.avatar : ''} />
          </div>
        )}
      </div>
      {isLumaBot && <Typography className={classes.interlocutor}>{lumaBotSettings.name}</Typography>}
    </div>
  );
};

export default withStyles(styles)(Message);
